import { Component, inject } from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import axios, { AxiosError } from 'axios';
import { HelpersService } from 'src/app/services/helpers/helpers.service';
import { environment } from 'src/environments/environment';
import { AdminCaseTableItem } from '../cases.component';
import { PayeePopupComponent } from '../../../../shared/payee-popup/payee-popup.component';
import { PayeeFormFieldComponent } from '../../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeDropdownComponent } from '../../../../shared/payee-form-fields/payee-dropdown/payee-dropdown.component';
import { PayeeButtonComponent } from '../../../../shared/payee-buttons/payee-button/payee-button.component';
import { TranslateModule } from '@ngx-translate/core';

type RecipientOption = { value: 'USER' | 'ATTORNEY'; label: string };

@Component({
  selector: 'app-send-custom-message-dialog',
  templateUrl: './send-custom-message-dialog.component.html',
  styleUrls: ['./send-custom-message-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    PayeeDropdownComponent,
    PayeeButtonComponent,
    TranslateModule,
  ],
})
export class SendCustomMessageDialogComponent {
  private fb = inject(FormBuilder);
  private dialogRef =
    inject<MatDialogRef<SendCustomMessageDialogComponent>>(MatDialogRef);
  private helpersService = inject(HelpersService);
  private snackbar = inject(MatSnackBar);

  data = inject<AdminCaseTableItem>(MAT_DIALOG_DATA);

  loading = false;

  readonly recipientOptions: RecipientOption[] = [
    { value: 'USER', label: 'Hitelező' },
    { value: 'ATTORNEY', label: 'Ügyvéd' },
  ];

  readonly form = this.fb.group({
    recipient: this.fb.control(this.recipientOptions[0], {
      validators: Validators.required,
      nonNullable: true,
    }),
    message: this.fb.control('', {
      validators: [Validators.required, Validators.maxLength(10000)],
      nonNullable: true,
    }),
  });

  readonly recipientDisplayWith = (opt?: RecipientOption): string =>
    opt?.label ?? '';

  async submit(): Promise<void> {
    if (this.form.invalid || this.loading) {
      this.helpersService.markAllChildrenAsDirty(this.form);
      return;
    }

    const { recipient, message } = this.form.getRawValue();

    try {
      this.loading = true;
      await axios.post(
        `${environment.baseUrl}/api/case/${this.data.id}/send-custom-message`,
        {
          recipient: recipient.value,
          message,
        },
      );
      this.dialogRef.close(true);
    } catch (error: AxiosError | unknown) {
      console.error('Error while sending custom message', error);
      let errorMessage = 'Ismeretlen hiba';
      if (error instanceof AxiosError && error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      this.snackbar.open(errorMessage, 'OK', { duration: 5000 });
    } finally {
      this.loading = false;
    }
  }

  readonly cancel = (): void => {
    this.dialogRef.close();
  };
}
